import { cn } from '@/utils/cn';
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes } from 'react-icons/fa';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
  children: React.ReactNode;
  hideCloseButton?: boolean;
  disableCloseOnClickOutside?: boolean;
}

export const Modal = ({
  isOpen,
  onClose,
  title,
  children,
  className,
  hideCloseButton = false,
  disableCloseOnClickOutside = false,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target as Node) &&
        !document
          .querySelector('.react-select__menu')
          ?.contains(event.target as Node) &&
        !hideCloseButton &&
        !disableCloseOnClickOutside
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose, hideCloseButton, disableCloseOnClickOutside]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden bg-secondary/50 p-4 backdrop-blur-md">
      <div
        ref={modalRef}
        className={cn(
          'relative w-full max-w-md rounded-lg bg-primary p-2 shadow-lg',
          'max-h-[calc(100vh-2rem)] min-h-[200px]',
          className,
        )}
      >
        {title && !hideCloseButton && (
          <div className="absolute right-4 top-4 text-gray-dark hover:text-danger">
            <button onClick={onClose} aria-label="Close">
              <FaTimes className="size-6" />
            </button>
          </div>
        )}

        <div className="p-2">
          {title && (
            <div className="mb-4 p-2 pr-8">
              <h2 className="text-xl font-semibold">{title}</h2>
            </div>
          )}

          <div
            ref={contentRef}
            className="relative h-full overflow-y-auto"
            style={{
              maxHeight: title
                ? 'calc(100vh - 2rem - 4rem - 1.5rem)'
                : 'calc(100vh - 2rem)',
            }}
          >
            <div className="p-2">{children}</div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
